import { GlobalOutlined } from '@ant-design/icons';
import { Select, Space } from 'antd';
import type React from 'react';
import { useTranslation } from 'react-i18next';
import { baseLanguage } from '../i18n/locale';

const { Option } = Select;

const languages = [
  { code: 'hu', name: 'Magyar', flag: '🇭🇺' },
  { code: 'en', name: 'English', flag: '🇬🇧' },
  { code: 'sr', name: 'Српски', flag: '🇷🇸' },
];

const LanguageSwitcher: React.FC = () => {
  const { i18n } = useTranslation();

  // A browser-detected language arrives region-qualified (`en-US`, `nl-NL`) and
  // may not be one we ship at all. Anything without an option of its own is
  // shown as English, matching where `fallbackLng` actually sent the interface —
  // otherwise antd renders the bare code (`nl`) with no flag or name.
  const base = baseLanguage(i18n.language);
  const current = languages.some((lang) => lang.code === base) ? base : 'en';

  // `onSelect`, not `onChange`: under a language we do not ship the Select
  // already displays "English" while i18next is still on `nl-NL`, and antd
  // suppresses `onChange` when the picked value equals the displayed one — so
  // choosing English did nothing and the stored language stayed `nl-NL`.
  // `onSelect` fires on every pick, including a re-pick of the shown option.
  const handleSelect = (value: string) => {
    try {
      i18n.changeLanguage(value);
      localStorage.setItem('i18nextLng', value);
    } catch (error) {
      console.error('Failed to change language:', error);
    }
  };

  return (
    <Select
      value={current}
      onSelect={handleSelect}
      style={{ width: 120 }}
      suffixIcon={<GlobalOutlined />}
    >
      {languages.map((lang) => (
        <Option key={lang.code} value={lang.code}>
          <Space>
            <span>{lang.flag}</span>
            <span>{lang.name}</span>
          </Space>
        </Option>
      ))}
    </Select>
  );
};

export default LanguageSwitcher;
